import Microgame from '../engine/Microgame.js';

export default class IgniteGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Allume !`, en: `Ignite!`, es: `¡Enciende!` },
            controls: { fr: `Glisse pour frotter l'allumette`, en: `Swipe to strike the match`, es: `Desliza para raspar la cerilla` },
            objective: { fr: `Crée une étincelle et allume le feu`, en: `Create a spark to light the fire`, es: `Crea una chispa para encender el fuego` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c-1.38 0-2-1.62-2-3a3 3 0 1 1 5 2.5v1.5a3.5 3.5 0 0 1-7 0"/><path d="M12 22v-3"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 84, toX: 50, toY: 28, duration: 520 },
                { type: 'swipe', fromX: 50, fromY: 78, toX: 50, toY: 30, duration: 520 },
                { type: 'swipe', fromX: 50, fromY: 76, toX: 50, toY: 32, duration: 520 },
                { type: 'wait', duration: 460 },
            ],
            loopDuration: 3600
        };
    }
    constructor(engine) {
        super(engine);
        this.match = null;
        this.matchHead = null;
        this.flame = null;
        this.striker = null;
        
        this.heat = 0;
        this.isDragging = false;
        
        this.startX = 0;
        this.startY = 0;
        this.matchX = 0;
        this.matchY = 0;
        
        this._isVictoryAnimating = false;
        this._frozenTimerValue = 0;
        
        this.strikerRect = { x: 0, y: 0, w: 0, h: 0 };
        
        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;
        // Modified scale factor (0.76) to reduce time at x4 by another ~500ms
        const adjustedSpeedScale = 1 + (speedMult - 1) * 0.76; 
        this.duration = 2250 * (speedMult / adjustedSpeedScale);
    }

    setup() {
        this.engine.ui.instruction.textContent = "STRIKE IT!";

        const containerRect = this.container.getBoundingClientRect();
        const width = containerRect.width > 0 ? containerRect.width : window.innerWidth;
        const height = containerRect.height > 0 ? containerRect.height : window.innerHeight;

        // Striker
        this.striker = document.createElement('div');
        this.striker.style.position = 'absolute';
        
        // Randomize striker size (horizontal or vertical)
        const isVertical = this.isPlayback ? true : (Math.random() > 0.5);
        this.strikerRect.w = isVertical ? 70 : 220;
        this.strikerRect.h = isVertical ? 220 : 70;
        
        // Random position, keeping it somewhat centered
        this.strikerRect.x = this.isPlayback
            ? (width / 2) - (this.strikerRect.w / 2)
            : ((width / 2) + ((Math.random() - 0.5) * (width * 0.4)) - (this.strikerRect.w / 2));
        this.strikerRect.y = this.isPlayback
            ? (height * 0.36) - (this.strikerRect.h / 2)
            : ((height / 2) + ((Math.random() - 0.5) * (height * 0.4)) - (this.strikerRect.h / 2) - 40);

        this.striker.style.width = `${this.strikerRect.w}px`;
        this.striker.style.height = `${this.strikerRect.h}px`;
        this.striker.style.left = `${this.strikerRect.x}px`;
        this.striker.style.top = `${this.strikerRect.y}px`;
        this.striker.style.background = '#222';
        this.striker.style.border = `2px dashed ${this.secondaryColor}`;
        this.striker.style.borderRadius = '10px';
        this.striker.style.boxShadow = `0 0 15px rgba(0,0,0,0.5)`;
        
        const texture = document.createElement('div');
        texture.style.width = '100%';
        texture.style.height = '100%';
        texture.style.backgroundImage = `repeating-linear-gradient(45deg, ${this.secondaryColor} 0, ${this.secondaryColor} 2px, transparent 2px, transparent 8px)`;
        texture.style.opacity = '0.4';
        texture.style.borderRadius = '8px';
        this.striker.appendChild(texture);
        this.container.appendChild(this.striker);

        // Match
        this.matchX = width / 2;
        this.matchY = height * 0.85;

        this.match = document.createElement('div');
        this.match.style.position = 'absolute';
        this.match.style.width = '16px'; 
        this.match.style.height = '140px';
        this.match.style.background = '#e6c280'; // Wood color
        this.match.style.left = `${this.matchX}px`;
        this.match.style.top = `${this.matchY}px`;
        this.match.style.transform = `translate(-50%, -50%)`;
        this.match.style.borderRadius = '8px';
        this.match.style.boxShadow = `2px 5px 10px rgba(0,0,0,0.4)`;
        this.match.style.cursor = 'grab';
        this.match.style.touchAction = 'none';
        this.match.style.zIndex = '10';

        // Hitbox for easier grabbing
        const hitbox = document.createElement('div');
        hitbox.style.position = 'absolute';
        hitbox.style.width = '60px'; // Wide hitbox
        hitbox.style.height = '180px'; // Tall hitbox
        hitbox.style.left = '50%';
        hitbox.style.top = '50%';
        hitbox.style.transform = 'translate(-50%, -50%)';
        hitbox.style.cursor = 'inherit';
        this.match.appendChild(hitbox);

        // Match Head
        this.matchHead = document.createElement('div');
        this.matchHead.style.position = 'absolute';
        this.matchHead.style.width = '26px';
        this.matchHead.style.height = '35px';
        this.matchHead.style.background = this.primaryColor;
        this.matchHead.style.top = '-10px';
        this.matchHead.style.left = '50%';
        this.matchHead.style.transform = 'translate(-50%, 0)';
        this.matchHead.style.borderRadius = '15px 15px 10px 10px';
        this.matchHead.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.matchHead.style.transition = 'background 0.1s, box-shadow 0.1s';
        this.match.appendChild(this.matchHead);

        // Flame
        this.flame = document.createElement('div');
        this.flame.style.position = 'absolute';
        this.flame.style.width = '60px';
        this.flame.style.height = '90px';
        this.flame.style.background = `radial-gradient(ellipse at bottom, white 10%, ${this.primaryColor} 40%, ${this.secondaryColor} 80%, transparent 100%)`;
        this.flame.style.top = '-80px';
        this.flame.style.left = '50%';
        this.flame.style.marginLeft = '-30px';
        this.flame.style.borderRadius = '50% 50% 20% 20%';
        this.flame.style.transform = 'scale(0)';
        this.flame.style.transformOrigin = 'bottom center';
        this.flame.style.filter = 'drop-shadow(0 0 20px white)';
        this.match.appendChild(this.flame);

        this.container.appendChild(this.match);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.match.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (this._isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }

        if (!this.active || this.won || this.lost) return;

        // Cool down if not rubbing
        if (this.heat > 0) {
            this.heat -= dt * 0.04; 
            if (this.heat < 0) this.heat = 0;
            
            const heatRatio = Math.min(1, this.heat / 100);
            this.matchHead.style.filter = `brightness(${1 + heatRatio * 1.5})`;
            this.matchHead.style.boxShadow = `0 0 ${10 + heatRatio * 30}px ${this.primaryColor}`;
        }
    }

    onDown(e) {
        if (!this.active || this.won || this.lost) return;
        this.isDragging = true;
        
        const rect = this.match.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        
        this.matchX = (rect.left + rect.width / 2) - containerRect.left;
        this.matchY = (rect.top + rect.height / 2) - containerRect.top;
        
        this.match.style.left = `${this.matchX}px`;
        this.match.style.top = `${this.matchY}px`;
        
        this.startX = e.clientX;
        this.startY = e.clientY;
        
        this.match.style.cursor = 'grabbing';
    }

    onMove(e) {
        if (!this.isDragging || !this.active || this.won) return;
        
        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;
        
        this.matchX += dx;
        this.matchY += dy;
        
        this.match.style.left = `${this.matchX}px`;
        this.match.style.top = `${this.matchY}px`;
        
        this.startX = e.clientX;
        this.startY = e.clientY;

        // Collision logic
        const headX = this.matchX;
        const headY = this.matchY - 60; // Approximate head center relative to match center

        // Check if head is inside strikerRect
        const margin = 10;
        if (headX > this.strikerRect.x - margin && headX < this.strikerRect.x + this.strikerRect.w + margin &&
            headY > this.strikerRect.y - margin && headY < this.strikerRect.y + this.strikerRect.h + margin) {
            
            // Calculate distance moved this frame
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist > 0) {
                // Generates heat based on distance rubbed
                this.heat += dist * 0.6; 
                this.createSpark(headX, headY);
                
                // Max heat triggers win
                if (this.heat >= 100) {
                    this.ignite();
                }
            }
        }
    }

    createSpark(x, y) {
        if (Math.random() > 0.4) return;
        const spark = document.createElement('div');
        spark.style.position = 'absolute';
        spark.style.width = '5px';
        spark.style.height = '5px';
        spark.style.background = '#fff';
        spark.style.borderRadius = '50%';
        spark.style.left = `${x + (Math.random() * 20 - 10)}px`;
        spark.style.top = `${y + (Math.random() * 20 - 10)}px`;
        spark.style.pointerEvents = 'none';
        spark.style.boxShadow = `0 0 10px #ffeb3b, 0 0 20px ${this.primaryColor}`;
        spark.style.zIndex = '15';
        this.container.appendChild(spark);

        requestAnimationFrame(() => {
            spark.style.transition = 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            spark.style.transform = `translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px) scale(0)`;
            spark.style.opacity = '0';
        });

        setTimeout(() => spark.remove(), 500);
    }

    ignite() {
        if (this.won || this._isVictoryAnimating) return;
        
        // Block interaction and freeze timer
        this._isVictoryAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        this.matchHead.style.filter = `brightness(2.5)`;
        
        // Immediate flare-up
        this.flame.style.transition = 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.flame.style.transform = 'scale(1.5)';
        
        setTimeout(() => {
            this.flame.style.transition = '';
            this.flame.style.transform = 'scale(1)';
            
            // Animate continuous flicker
            this.flame.animate([
                { transform: 'scale(1) rotate(-5deg)' },
                { transform: 'scale(1.1) rotate(5deg)' },
                { transform: 'scale(0.95) rotate(-3deg)' },
                { transform: 'scale(1.05) rotate(4deg)' },
                { transform: 'scale(1) rotate(0deg)' }
            ], {
                duration: 400,
                iterations: Infinity
            });
            
            // Allow player to enjoy the fire briefly, then win
            setTimeout(() => {
                this._isVictoryAnimating = false; // Just in case
                this.won = true;
                this.win();
            }, 600);
            
        }, 200);
    }

    onUp() {
        this.isDragging = false;
        if (this.match) this.match.style.cursor = 'grab';
    }

    cleanup() {
        super.cleanup();
        if (this.match) this.match.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
